import ApplicationCulture from '../config/ApplicationCulture.js';
import TypeHelper from './TypeHelper.js';

const NumberHelper = (function()  {

    /**
     * Máximo de dígitos que um número pode ter nessa aplicação.
     * @public
     * @constant
     * @type {number}
     */
    const MaxDigits = 16;
    /**
     * Valor mínimo monetário aceitável na aplicação -99 trilhões.
     * @public
     * @constant
     * @field
     * @type {number}
     */
    const _minMoney = -99999999999999.99;
    /**
     * Valor máximo monetário aceitável na aplicação 99 trilhões.
     * @public
     * @constant
     * @field
     * @type {number}
     */
    const _maxMoney = 99999999999999.99;

    /**
     * Verifica se o dado informado é um {@link Number} finito válido.
     * @public
     * @function
     * @param {number} value Qualquer tipo de dado.
     * @returns {boolean} Se o dado informado é um {@link Number} finito válido.
    */
    const IsValid = (value) => {
        return (
            TypeHelper.IsNumber(value)
            && !Number.isNaN(value)
            && isFinite(value)
        );
    };

    /**
     * Verifica se o dado informado é um {@link Number} menor que 1.
     * @public
     * @function
     * @param {number} value - Qualquer tipo de dado.
     * @returns {boolean} Se dado informado é um {@link Number} menor que 1.
     */
    const IsLessThanOne = (value) => {
        return (
            !IsValid(value)
            || value < 1
        );
    };

    /**
     * Verifica se o dado informado é um {@link Number} negativo.
     * @public
     * @function
     * @param {number} value - Dado de qualquer tipo.
     * @returns {boolean} Se o dado informado é um {@link Number} negativo.
     */
    const IsNegative = (value) => {
        return (
            !IsValid(value)
            || value < 0
        );
    };

    /**
     * Verifica se o valor informado é um valor monetário aceitável pela aplicação.
     * @public
     * @function
     * @param {number} value Valor a ser analisado.
     * @returns {Boolean} Se o valor informado é um valor monetário aceitável pela aplicação.
     */
    const IsValidCurrency = (value) => {
        return (
            IsValid(value)
            && (value >= _minMoney && value <= _maxMoney)
        );
    };

    /**
     * Converte algum tipo de dado em number.
     * @public
     * @function
     * @param {any} data O dado que será convertido em number.
     * @param {boolean} [asInteger = false] Força o retorno ser um número inteiro caso seja válido.
     * @returns {number} O dado convertido em number ou NaN.
    */
    const ToNumber = (data, asInteger = false) => {

        if ( !TypeHelper.IsBoolean(asInteger) ) asInteger = false;

        if ( IsValid(data) ) {

            if ( asInteger ) return Math.trunc(data);
            return data;
        }

        if ( !TypeHelper.IsString(data) ) return NaN;
        
        let convertedNumber = parseFloat(data);

        return (
            isNaN(convertedNumber) 
            || !isFinite(convertedNumber) 
                ? NaN 
                : asInteger 
                    ? Math.trunc(convertedNumber) 
                    : convertedNumber
        );
    };

    /**
     * Elimina as casas deciamis que excederem a quantidade informada.
     * @public
     * @function
     * @param {number} num Número a ser tratado.
     * @param {number} digits Quantidade de dígitos decimais desejados.
     * @returns 
     */
    const Truncate = (num, digits) => {

        num = ToNumber(num, false);

        if ( isNaN(num) ) return 0;

        let numS = num.toString().trim();
        let decPos = numS.indexOf('.');
        let substrLength = decPos == -1 ? numS.length : 1 + decPos + digits;
        let trimmedResult = numS.substring(0, substrLength);
        let finalResult = isNaN(trimmedResult) ? 0 : trimmedResult;

        return parseFloat(finalResult);
    };

    /**
     * Converte uma string que representa um valor monetário em um número.
     * @public
     * @function
     * @param {string} value - Valor a ser convertido.
     * @returns {number} - Valor convertido em número.
     */
    const CurrencyStringToNumber = (value) => {

        if ( !TypeHelper.IsString(value) ) return NaN;

        switch ( ApplicationCulture.CurrentCulture.LCID ) {

            case ApplicationCulture.Brazil.LCID:
            default:
            {
                value = value.replace(/\./g, "").replaceAll(",", ".").replaceAll(/[^\d\.]/g, "").trim();

                return ToNumber(value, false);
            }
        }

    };

    const _helper = {
        IsValid,
        IsLessThanOne,
        IsNegative,
        IsValidCurrency,
        ToNumber,
        Truncate,
        CurrencyStringToNumber,
    };

    Object.defineProperty(_helper, "MaxDigits", {
        get: () => MaxDigits,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MinMoney", {
        get: () => _minMoney,
        set: () => {throw TypeError("Assignment to constant property.");}
    });
    Object.defineProperty(_helper, "MaxMoney", {
        get: () => _maxMoney,
        set: () => {throw TypeError("Assignment to constant property.");}
    });

    return Object.freeze(_helper);

})();

export default NumberHelper;